import React, { useState, useRef } from 'react';
import { Input, Badge } from 'reactstrap';
import { ModalButtonBuilder, ButtonBuilder, ToastBuilder, ApiRequest } from '../../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe } from '../../../../../../../../../GlobalComponents/Helpers';
import CreatableMultiSelect from '../../../../../../../../../GlobalComponents/Common/Component/CreatableMultiSelect';
import { ProgressLoader } from '../../../../../../../../../Layout/Loader';

/**
 * ResourceOptionsBuilder — Dynamic UI-driven modal for managing resource options JSON.
 *
 * Shows sections based on what keys exist in the current resource_options.
 * User can add/remove sections dynamically via pill buttons at the bottom.
 *
 * Known section types:
 *   - setup_ids          → CreatableMultiSelect (numeric IDs, fetches labels from API)
 *   - parent_resource_ids → CreatableMultiSelect (pre-loaded from parent's setup_ids)
 *   - args.conditions    → Text input for API query conditions
 *   - integrationOptions.setupField → Text input (FreshService field name)
 *   - args.*             → Generic key:value pairs
 */
const ResourceOptionsBuilder = ({
    row,
    connectionId,
    connectionData,
    jobData,
    jobResources,
    parentInfo,
    resourceSetValue,
    readOnly,
}) => {
    // Active sections state — tracks which sections are visible
    const [activeSections, setActiveSections] = useState(new Set());
    // Data state for each section
    const [setupIds, setSetupIds] = useState([]);
    const [parentResourceIds, setParentResourceIds] = useState([]);
    const [conditions, setConditions] = useState('');
    const [setupField, setSetupField] = useState('');
    const [customArgs, setCustomArgs] = useState([]); // [{key, value}]
    const [otherTopLevel, setOtherTopLevel] = useState({});

    // API values for label enrichment
    const [availableValues, setAvailableValues] = useState([]);
    const [parentValues, setParentValues] = useState([]);
    const [loadingValues, setLoadingValues] = useState(false);
    const valuesCache = useRef({});

    const isSetupOrSupp = row.is_setup_resource || row.resource_usage === 'setup' || row.resource_usage === 'supplementary';
    const isChild = parentInfo?.hasSetupIds;
    const serviceCode = connectionData?.service_code || '';
    const isFreshService = serviceCode === 'freshservice';

    const fieldName = 'resourceOptions' + row.resource_id;

    // Should we show this builder at all?
    if (!isSetupOrSupp && !isChild) return null;

    // All possible sections this builder knows about
    const KNOWN_SECTIONS = [
        { id: 'setup_ids', label: 'Setup IDs', showWhen: () => isSetupOrSupp && !isChild },
        { id: 'parent_resource_ids', label: 'Parent Resource IDs', showWhen: () => isChild },
        { id: 'conditions', label: 'Conditions (args)', showWhen: () => true },
        { id: 'setupField', label: 'Setup Field (args)', showWhen: () => isFreshService && isSetupOrSupp },
        { id: 'customArgs', label: 'Custom Arg', showWhen: () => true },
    ];

    /**
     * Parse current resource_options and activate sections based on what exists.
     */
    const loadCurrentOptions = () => {
        const el = document.querySelector(`textarea[name="${fieldName}"]`);
        const raw = el ? el.value : '';
        // Normalize: strip leading #, replace tabs/4-spaces with 2-space indent
        const normalized = raw.replace(/^#/, '').replace(/\t/g, '  ').replace(/ {4}/g, '  ').trim();
        const parsed = parseJSONSafe(normalized) || {};

        const sections = new Set();

        // setup_ids
        if ('setup_ids' in parsed) {
            sections.add('setup_ids');
            setSetupIds(Array.isArray(parsed.setup_ids) ? parsed.setup_ids.map(Number) : []);
        } else {
            setSetupIds([]);
        }

        // parent_resource_ids
        if ('parent_resource_ids' in parsed) {
            sections.add('parent_resource_ids');
            setParentResourceIds(Array.isArray(parsed.parent_resource_ids) ? parsed.parent_resource_ids.map(Number) : []);
        } else {
            setParentResourceIds([]);
        }

        // args
        const argsObj = parsed.args && typeof parsed.args === 'object' ? parsed.args : {};

        // args.conditions
        if ('conditions' in argsObj) {
            sections.add('conditions');
            setConditions(argsObj.conditions || '');
        } else {
            setConditions('');
        }

        // integrationOptions.setupField (top-level directive channel, sibling of args)
        const intOpts = parsed.integrationOptions && typeof parsed.integrationOptions === 'object' ? parsed.integrationOptions : {};
        if ('setupField' in intOpts) {
            sections.add('setupField');
            setSetupField(intOpts.setupField || '');
        } else {
            setSetupField('');
        }

        // Collect other args as custom key:value pairs
        const { conditions: _c, ...restArgs } = argsObj;
        const { setupField: _sf, ...restIntOpts } = intOpts;
        const custom = [];
        // Other integrationOptions (besides setupField), e.g. nestedLevel
        for (const [k, v] of Object.entries(restIntOpts)) {
            custom.push({ key: `integrationOptions.${k}`, value: typeof v === 'object' ? JSON.stringify(v) : String(v) });
        }
        // Other args (besides conditions)
        for (const [k, v] of Object.entries(restArgs)) {
            custom.push({ key: k, value: typeof v === 'object' ? JSON.stringify(v) : String(v) });
        }
        if (custom.length > 0) sections.add('customArgs');
        setCustomArgs(custom);

        // Preserve top-level keys we don't manage
        const { setup_ids, parent_resource_ids, args, integrationOptions, ...restTop } = parsed;
        setOtherTopLevel(restTop);

        setActiveSections(sections);
    };

    /**
     * Fetch supplementary values for label enrichment.
     */
    const fetchValues = async () => {
        const el = document.querySelector(`textarea[name="${fieldName}"]`);
        const raw = el ? el.value : '';
        const parsed = parseJSONSafe(raw) || {};

        const needsSetupValues = 'setup_ids' in parsed && isSetupOrSupp && !isChild;
        const needsParentValues = isChild;

        const resourceIdToFetch = needsSetupValues ? row.resource_id
            : (needsParentValues ? row.parent_resource_id : null);
        if (!resourceIdToFetch) return;

        const cacheKey = `${connectionId}-${resourceIdToFetch}`;
        if (valuesCache.current[cacheKey]) {
            if (resourceIdToFetch === row.resource_id) {
                setAvailableValues(valuesCache.current[cacheKey]);
            } else {
                setParentValues(valuesCache.current[cacheKey]);
            }
            return;
        }

        setLoadingValues(true);
        try {
            const results = await ApiRequest(
                'GET', '/dmt/jobs/{jobId}/supplementaryData/values',
                {
                    pathParams: { jobId: jobData.id },
                    queryParams: { connectionId, resourceId: resourceIdToFetch },
                }
            );
            const values = results.data || [];
            valuesCache.current[cacheKey] = values;
            if (resourceIdToFetch === row.resource_id) {
                setAvailableValues(values);
            } else {
                setParentValues(values);
            }
        } catch (e) {
            console.warn('[ResourceOptionsBuilder] Failed to fetch values:', e);
        } finally {
            setLoadingValues(false);
        }
    };

    /**
     * Reconstruct resource_options JSON from active sections and write back.
     */
    const applyToTextarea = () => {
        const result = { ...otherTopLevel };

        if (activeSections.has('setup_ids')) {
            result.setup_ids = setupIds;
        }
        if (activeSections.has('parent_resource_ids')) {
            result.parent_resource_ids = parentResourceIds;
        }

        // Build args object
        const argsResult = {};
        if (activeSections.has('conditions') && conditions.trim()) {
            argsResult.conditions = conditions.trim();
        }

        // integrationOptions — top-level directive channel (setupField, nestedLevel, …)
        const intOpts = {};
        if (activeSections.has('setupField') && setupField.trim()) {
            intOpts.setupField = setupField.trim();
        }
        // Re-inject other integrationOptions from custom args
        const customArgsFiltered = [];
        for (const arg of customArgs) {
            if (arg.key.startsWith('integrationOptions.')) {
                const subKey = arg.key.replace('integrationOptions.', '');
                if (subKey) {
                    let val = arg.value;
                    try { val = JSON.parse(val); } catch {}
                    intOpts[subKey] = val;
                }
            } else if (arg.key.trim()) {
                customArgsFiltered.push(arg);
            }
        }

        // Other custom args go into args
        for (const arg of customArgsFiltered) {
            if (arg.key.trim()) {
                let val = arg.value;
                try { val = JSON.parse(val); } catch {}
                argsResult[arg.key.trim()] = val;
            }
        }

        if (Object.keys(argsResult).length > 0) {
            result.args = argsResult;
        }

        // FreshService setup resources require integrationOptions (setupField) to exist
        if (Object.keys(intOpts).length > 0 || (isFreshService && isSetupOrSupp)) {
            result.integrationOptions = intOpts;
        }

        const newValue = Object.keys(result).length > 0
            ? '#' + JSON.stringify(result, null, 2)
            : '';
        resourceSetValue(fieldName, newValue, { shouldDirty: true, shouldValidate: true });

        setTimeout(() => {
            const el = document.querySelector(`textarea[name="${fieldName}"]`);
            if (el) {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
                nativeInputValueSetter.call(el, newValue);
                el.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }, 0);
    };

    const handleApply = () => {
        applyToTextarea();
        ToastBuilder('success', 'Resource options applied');
    };

    // Section management
    const addSection = (sectionId) => {
        setActiveSections(prev => new Set([...prev, sectionId]));
        // Initialize defaults for newly added sections
        if (sectionId === 'customArgs' && customArgs.length === 0) {
            setCustomArgs([{ key: '', value: '' }]);
        }
    };

    const removeSection = (sectionId) => {
        setActiveSections(prev => {
            const next = new Set(prev);
            next.delete(sectionId);
            return next;
        });
        // Clear data for removed sections
        if (sectionId === 'setup_ids') setSetupIds([]);
        if (sectionId === 'parent_resource_ids') setParentResourceIds([]);
        if (sectionId === 'conditions') setConditions('');
        if (sectionId === 'setupField') setSetupField('');
        if (sectionId === 'customArgs') setCustomArgs([]);
    };

    // Custom args management
    const addCustomArg = () => setCustomArgs(prev => [...prev, { key: '', value: '' }]);
    const updateCustomArg = (index, field, value) => setCustomArgs(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
    const removeCustomArg = (index) => {
        setCustomArgs(prev => {
            const next = prev.filter((_, i) => i !== index);
            if (next.length === 0) removeSection('customArgs');
            return next;
        });
    };

    // Build options for CreatableMultiSelect
    const buildSetupIdOptions = () => {
        if (!availableValues || availableValues.length === 0) return undefined;
        return availableValues.map(v => ({
            label: `${v.value_id} : ${v.value}`,
            value: v.value_id,
        }));
    };

    const buildParentIdOptions = () => {
        if (!parentInfo?.setupIds?.length) return undefined;
        return parentInfo.setupIds.map(id => {
            const numId = Number(id);
            const matchedValue = parentValues.find(v => Number(v.value_id) === numId);
            return {
                label: matchedValue ? `${numId} : ${matchedValue.value}` : String(numId),
                value: numId,
            };
        });
    };

    // Available sections that can be added (not yet active)
    const getAddableSections = () => {
        return KNOWN_SECTIONS.filter(s => s.showWhen() && !activeSections.has(s.id));
    };

    return (
        <ModalButtonBuilder
            title='Options Builder'
            type='button'
            className='btn-info btn-sm'
            buttonStyle={{ minWidth: 130, marginTop: 4 }}
            modalTitle={`Resource Options — ${row.name}`}
            size='md'
            onSubmit={readOnly ? false : 'Apply'}
            onCancelLabel={readOnly ? 'Close' : 'Cancel'}
            customSubmit={readOnly ? null : (toggler) => {
                handleApply();
                toggler();
            }}
            onClick={() => {
                loadCurrentOptions();
                fetchValues();
            }}
            body={
                <div style={{ minHeight: 100 }}>

                    {/* Setup IDs */}
                    {activeSections.has('setup_ids') && (
                        <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginBottom: 12, position: 'relative' }}>
                            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 0 }}>Setup IDs</label>
                                {!readOnly && <span style={{ cursor: 'pointer', color: '#dc3545', fontSize: 16, lineHeight: 1 }} onClick={() => removeSection('setup_ids')} title='Remove'>×</span>}
                            </div>
                            {loadingValues ? <ProgressLoader /> : (
                                <CreatableMultiSelect
                                    value={setupIds}
                                    onChange={(values) => setSetupIds(values.map(Number).filter(n => !isNaN(n)))}
                                    options={buildSetupIdOptions()}
                                    placeholder={availableValues.length > 0 ? 'Select or type IDs...' : 'Type numeric IDs and press Enter...'}
                                    disabled={readOnly}
                                    autoNumeric={true}
                                    size='md'
                                />
                            )}
                            {availableValues.length === 0 && !loadingValues && (
                                <p style={{ fontSize: 11, color: '#999', marginTop: 6, marginBottom: 0 }}>
                                    No imported values found. Type IDs manually, or run "Refresh Values" to import.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Parent Resource IDs */}
                    {activeSections.has('parent_resource_ids') && (
                        <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginBottom: 12, position: 'relative' }}>
                            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 0 }}>
                                    Parent Resource IDs
                                    <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 8 }}>from {parentInfo.parentName}</span>
                                </label>
                                {!readOnly && <span style={{ cursor: 'pointer', color: '#dc3545', fontSize: 16, lineHeight: 1 }} onClick={() => removeSection('parent_resource_ids')} title='Remove'>×</span>}
                            </div>
                            {loadingValues ? <ProgressLoader /> : (
                                <CreatableMultiSelect
                                    value={parentResourceIds}
                                    onChange={(values) => setParentResourceIds(values.map(Number).filter(n => !isNaN(n)))}
                                    options={buildParentIdOptions()}
                                    placeholder='Select parent IDs or type new ones...'
                                    disabled={readOnly}
                                    autoNumeric={true}
                                    size='md'
                                />
                            )}
                            {!readOnly && parentInfo?.setupIds?.length > 0 && (
                                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                                    <ButtonBuilder label='Select All' type='button' className='btn-outline-primary btn-sm' useSpinner={false}
                                        onClick={() => setParentResourceIds([...parentInfo.setupIds.map(Number)])} />
                                    <ButtonBuilder label='Clear' type='button' className='btn-outline-secondary btn-sm' useSpinner={false}
                                        onClick={() => setParentResourceIds([])} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Conditions (args.conditions) */}
                    {activeSections.has('conditions') && (
                        <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 0 }}>
                                    Conditions
                                    <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 6 }}>args.conditions</span>
                                </label>
                                {!readOnly && <span style={{ cursor: 'pointer', color: '#dc3545', fontSize: 16, lineHeight: 1 }} onClick={() => removeSection('conditions')} title='Remove'>×</span>}
                            </div>
                            <Input
                                type='textarea'
                                bsSize='sm'
                                value={conditions}
                                readOnly={readOnly}
                                onChange={(e) => setConditions(e.target.value)}
                                placeholder="e.g. projectFlag = false and id IN (0)"
                                style={{ height: 55, fontSize: 12, fontFamily: 'monospace' }}
                            />
                        </div>
                    )}

                    {/* Setup Field (integrationOptions.setupField) */}
                    {activeSections.has('setupField') && (
                        <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 0 }}>
                                    Setup Field
                                    <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 6 }}>integrationOptions.setupField</span>
                                </label>
                                {!readOnly && <span style={{ cursor: 'pointer', color: '#dc3545', fontSize: 16, lineHeight: 1 }} onClick={() => removeSection('setupField')} title='Remove'>×</span>}
                            </div>
                            <Input
                                bsSize='sm'
                                value={setupField}
                                readOnly={readOnly}
                                onChange={(e) => setSetupField(e.target.value)}
                                placeholder='e.g. source, status, category'
                                style={{ fontSize: 12 }}
                            />
                        </div>
                    )}

                    {/* Custom Args (args.*) */}
                    {activeSections.has('customArgs') && (
                        <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 0 }}>
                                    Custom Args
                                    <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 6 }}>args.*</span>
                                </label>
                                <div className='d-flex align-items-center gap-2'>
                                    {!readOnly && <ButtonBuilder label='+ Add' type='button' className='btn-outline-secondary btn-sm' useSpinner={false} onClick={addCustomArg} />}
                                    {!readOnly && <span style={{ cursor: 'pointer', color: '#dc3545', fontSize: 16, lineHeight: 1 }} onClick={() => removeSection('customArgs')} title='Remove all'>×</span>}
                                </div>
                            </div>
                            {customArgs.map((arg, i) => (
                                <div key={i} className='d-flex align-items-center gap-2 mb-1'>
                                    <Input bsSize='sm' placeholder='Key' value={arg.key} readOnly={readOnly}
                                        style={{ flex: '0 0 140px', fontSize: 12 }}
                                        onChange={(e) => updateCustomArg(i, 'key', e.target.value)} />
                                    <Input bsSize='sm' placeholder='Value' value={arg.value} readOnly={readOnly}
                                        style={{ flex: '1 1 auto', fontSize: 12 }}
                                        onChange={(e) => updateCustomArg(i, 'value', e.target.value)} />
                                    {!readOnly && <span style={{ cursor: 'pointer', color: '#dc3545', fontSize: 14 }} onClick={() => removeCustomArg(i)}>×</span>}
                                </div>
                            ))}
                            {customArgs.length === 0 && <p style={{ fontSize: 12, color: '#888', margin: 0 }}>No custom args</p>}
                        </div>
                    )}

                    {/* Add Section pills */}
                    {!readOnly && getAddableSections().length > 0 && (
                        <div style={{ paddingTop: 6, borderTop: '1px solid var(--bs-border-color, #dee2e6)' }}>
                            <span style={{ fontSize: 11, color: '#888', marginRight: 8 }}>Add:</span>
                            {getAddableSections().map(section => (
                                <Badge
                                    key={section.id}
                                    color='secondary'
                                    style={{ cursor: 'pointer', marginRight: 6, marginBottom: 4, fontSize: 11, padding: '4px 8px' }}
                                    onClick={() => addSection(section.id)}
                                >
                                    + {section.label}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default ResourceOptionsBuilder;
